import React, { useState, useMemo } from 'react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SignedAvatarImage } from '@/components/ui/signed-avatar-image';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ListPagination } from '@/components/ListPagination';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import { useTableSort } from '@/hooks/useTableSort';
import { StatCard, CurrencyDisplay } from '@/components/ui/stat-card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { captureError } from "@/lib/logger";
import { fetchAllRows } from '@/lib/fetchAllRows';
import { outstandingDriverIdentity } from '@/components/contracts/repaymentsDriver';
import { CollectionActionSheet, CollectionActionType } from '@/components/contracts/CollectionActionSheet';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card';
import { format, parse, differenceInDays, startOfWeek, endOfWeek } from 'date-fns';
import { MoreHorizontal, ShieldCheck, StickyNote, ChevronDown, ChevronRight, Filter } from 'lucide-react';

interface ContractRepaymentsOutstandingProps {
    contractId?: number;
    monthRange: { from: Date; to: Date };
}

interface ContractTransaction {
    id: number;
    amount: number;
    transaction_at: string;
    description: string | null;
}

interface InstalmentWeek {
    weekStart: string;
    weekEnd: string;
    expectedAmount: number;
    paidAmount: number;
    transactions: ContractTransaction[];
    status: 'paid' | 'partial' | 'missed';
}

interface OutstandingContract {
    contract_id: number;
    driver_name: string;
    driver_photo: string | null;
    phone_number: string | null;
    vehicle_registration: string | null;
    vehicle_color: string | null;
    vehicle_listing_url: string | null;
    vehicle_updated_at: string | null;
    expected: number;
    paid: number;
    shortfall: number;
    last_payment: string | null;
    transactions: ContractTransaction[];
    instalmentWeeks: InstalmentWeek[];
}

const PAGE_SIZE = 20;

const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(amount);

function getSbtPhotoUrl(listingUrl: string | null, updatedAt: string | null): string | null {
    if (!listingUrl) return null;
    const match = listingUrl.match(/sbtjapan\.com\/used-cars\/([A-Z0-9]+)/i);
    if (!match) return null;
    const cacheBust = updatedAt ? `?v=${new Date(updatedAt).getTime()}` : '';
    return `https://img.sbtjapan.com/img/carphoto/${match[1]}/normal/f.jpg${cacheBust}`;
}

function getColorValue(color: string): string {
    const trimmed = color.trim();
    if (/^[0-9A-Fa-f]{6}$/.test(trimmed)) return `#${trimmed}`;
    if (trimmed.startsWith('#')) return trimmed;
    return trimmed;
}

export default function ContractRepaymentsOutstanding({ contractId, monthRange }: ContractRepaymentsOutstandingProps) {
    const [currentPage, setCurrentPage] = useState(1);

    const { sortKey, sortDirection, handleSort } = useTableSort({
        defaultSortKey: 'contract_id',
        defaultSortDirection: 'desc',
        onSortChange: () => setCurrentPage(1),
    });

    // Search and filter state
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<'ALL' | 'PARTIAL' | 'MISSED'>('ALL');

    const fromDate = format(monthRange.from, 'yyyy-MM-dd');
    const toDate = format(monthRange.to, 'yyyy-MM-dd');

    // Consolidated state for CollectionActionSheet - prevents stale state issues
    const [sheetData, setSheetData] = useState<{
        row: OutstandingContract;
        type: CollectionActionType;
        existingRecord: {
            id: number;
            contract_id: number;
            period_start: string;
            period_end: string;
            reason: string | null;
        } | null;
        fixedPeriod: { from: string; to: string } | null;
    } | null>(null);

    const sheetOpen = sheetData !== null;

    // State for expanded rows (to show transaction details)
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

    // Fetch existing waivers and notes for the period. Both are paged to the end: one request
    // stops at PostgREST's max-rows without saying so, and a waiver that does not arrive shows its
    // week as unwaived and offers it for a second one. A failed read throws instead of standing
    // in for "no waivers".
    const { data: collectionActions, error: collectionActionsError } = useQuery({
        queryKey: ['collection-actions', fromDate, toDate],
        queryFn: async () => {
            const [waivers, notes] = await Promise.all([
                fetchAllRows({
                    label: 'interest waivers',
                    orderedBy: 'id',
                    page: (from, to) => supabase
                        .from('repayment_interest_waivers')
                        .select('id, contract_id, period_start, period_end, reason', { count: 'exact' })
                        .gte('period_start', fromDate)
                        .lte('period_end', toDate)
                        .order('id', { ascending: true })
                        .range(from, to),
                    keyOf: (w) => w.id,
                }),
                fetchAllRows({
                    label: 'repayment notes',
                    orderedBy: 'id',
                    page: (from, to) => supabase
                        .from('repayment_notes')
                        .select('id, contract_id, period_start, period_end, reason', { count: 'exact' })
                        .gte('period_start', fromDate)
                        .lte('period_end', toDate)
                        .order('id', { ascending: true })
                        .range(from, to),
                    keyOf: (n) => n.id,
                }),
            ]);
            return {
                waivers: waivers as { id: number; contract_id: number; period_start: string; period_end: string; reason: string | null }[],
                notes: notes as { id: number; contract_id: number; period_start: string; period_end: string; reason: string | null }[],
            };
        },
    });

    // Helper to find existing waiver/note for a contract + specific week period
    const getWaiverForWeek = (cid: number, weekStart: string, weekEnd: string) =>
        collectionActions?.waivers.find(w =>
            w.contract_id === cid &&
            weekStart <= w.period_end &&
            weekEnd >= w.period_start
        );
    const getNoteForWeek = (cid: number, weekStart: string, weekEnd: string) =>
        collectionActions?.notes.find(n =>
            n.contract_id === cid &&
            weekStart <= n.period_end &&
            weekEnd >= n.period_start
        );


    // Open sheet handler - sets all data atomically to prevent stale state
    const openSheet = (
        row: OutstandingContract,
        type: CollectionActionType,
        existing?: { id: number; contract_id: number; period_start: string; period_end: string; reason: string | null } | null,
        weekDates?: { weekStart: string; weekEnd: string }
    ) => {
        setSheetData({
            row,
            type,
            existingRecord: existing || null,
            fixedPeriod: weekDates ? { from: weekDates.weekStart, to: weekDates.weekEnd } : null,
        });
    };

    // Close sheet handler
    const closeSheet = () => setSheetData(null);

    // Toggle row expansion
    const toggleExpand = (contractId: number) => {
        setExpandedRows(prev => {
            const next = new Set(prev);
            if (next.has(contractId)) {
                next.delete(contractId);
            } else {
                next.add(contractId);
            }
            return next;
        });
    };

    // Calculate number of weeks in the period (contract instalments are weekly)
    const daysInPeriod = differenceInDays(monthRange.to, monthRange.from) + 1;
    const weeksInPeriod = Math.max(1, Math.ceil(daysInPeriod / 7));

    // Fetch all active contracts with their expected and actual payments
    const { data: outstandingData, isLoading, error } = useQuery({
        queryKey: ['outstanding-contracts', fromDate, toDate, contractId],
        queryFn: async () => {
            // 1. Get contracts with driver info and status history
            let contractsQuery = supabase
                .from('contracts')
                .select(`
                    id,
                    drivers(profiles(id, admin_id, first_name, middle_name, last_name, phone, photo)),
                    vehicles(color, updated_at, vehicle_registrations(number), vehicle_procurement(listing_url)),
                    contract_updates(status, created_at, occurred_at)
                `);

            // Filter by specific contract if provided
            if (contractId) {
                contractsQuery = contractsQuery.eq('id', contractId);
            }

            const { data: contracts, error: contractsError } = await contractsQuery;

            if (contractsError) {
                captureError(contractsError);
                throw contractsError;
            }

            // Filter to only IN_PROGRESS contracts (based on latest update)
            // When filtering by specific contract, include it even if not IN_PROGRESS for visibility
            const activeContracts = (contracts || []).filter(c => {
                const updates = (c.contract_updates as any[]) || [];
                if (updates.length === 0) return contractId ? true : false; // Include filtered contract even without updates
                const latest = updates.sort((a, b) =>
                    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                )[0];
                return contractId ? true : latest?.status === 'IN_PROGRESS';
            });

            const activeContractIds = activeContracts.map(c => c.id);

            // 2. Get ALL contract instalments for active contracts (to find the applicable one for the period)
            const { data: allContractInstalments, error: ciError } = await supabase
                .from('contract_instalments')
                .select('contract_id, amount, starts_at')
                .in('contract_id', activeContractIds)
                .lte('starts_at', toDate) // Started on or before end of period
                .order('starts_at', { ascending: false });

            if (ciError) throw ciError;

            // Canonical "current weekly instalment" per contract: contract instalment + active
            // (not-fully-repaid) loan & insurance-loan instalments. Same logic as the contract
            // page's weekly_rate, so the two surfaces stay in sync and fully-repaid loans drop off
            // (e.g. a contract's first-year insurance loan stops being charged once it's paid off).
            const { data: weeklyRows, error: weeklyError } = await (supabase as any)
                .rpc('get_contracts_weekly_instalment', { p_contract_ids: activeContractIds });

            if (weeklyError) throw weeklyError;

            const canonicalWeeklyByContract = new Map<number, number>();
            (weeklyRows || []).forEach((r: any) => {
                canonicalWeeklyByContract.set(Number(r.contract_id), Number(r.weekly_instalment) || 0);
            });

            // 4. Get the active contracts' repayments for the period, every page of them. The date
            // filter does not keep this read under PostgREST's max-rows: a single request is cut at
            // the cap with no error, and the rows that went missing were the newest weeks, which then
            // showed as missed and were offered for waivers. `fetchAllRows` pages it in primary-key
            // order, so the pages neither repeat nor skip a row, and throws unless every row arrived.
            // Narrowing to the active contracts on the server (they used to be filtered here, in the
            // browser) makes the pages fewer; it does not make them unnecessary.
            const repayments = await fetchAllRows({
                label: 'collections repayments',
                orderedBy: 'id',
                page: (from, to) => supabase
                    .from('repayments')
                    .select('id, contract_id, transactions!inner(id, amount, transaction_at, description)', { count: 'exact' })
                    .in('contract_id', activeContractIds)
                    .gte('transactions.transaction_at', fromDate)
                    .lte('transactions.transaction_at', toDate + 'T23:59:59')
                    .order('id', { ascending: true })
                    .range(from, to),
                keyOf: (r) => r.id,
            });


            // Build maps for aggregation
            const expectedByContract = new Map<number, number>();
            const paidByContract = new Map<number, { amount: number; lastPayment: string | null }>();
            const transactionsByContract = new Map<number, ContractTransaction[]>();

            // Build a map of weekly instalment amounts per contract and their start dates
            const weeklyInstalmentByContract = new Map<number, number>();
            const contractStartDates = new Map<number, string>();
            const seenContracts = new Set<number>();
            allContractInstalments?.forEach(ci => {
                if (!seenContracts.has(ci.contract_id)) {
                    seenContracts.add(ci.contract_id);
                    weeklyInstalmentByContract.set(ci.contract_id, ci.amount || 0);
                    // Use first instalment's starts_at as contract start date
                    if (ci.starts_at) {
                        contractStartDates.set(ci.contract_id, ci.starts_at);
                    }
                }
            });

            // Build a map of termination end dates (occurred_at of TERMINATED update)
            const terminationEndDates = new Map<number, Date>();
            activeContracts.forEach(c => {
                const updates = (c.contract_updates as any[]) || [];
                const terminatedUpdate = updates.find((u: any) => u.status === 'TERMINATED');
                if (terminatedUpdate?.occurred_at) {
                    // occurred_at is timestamptz, returned as an ISO UTC string, so its first 10
                    // characters ARE the Ghana calendar day (the app-wide convention: the terminated
                    // day is occurred_at's Ghana date). Store that day as a local-midnight Date so it
                    // is on the same basis as starts_at and monthRange; keeping the raw instant made
                    // every downstream day count, week label and badge depend on the viewer's zone —
                    // a 23:30 UTC termination billed one extra week east of Accra.
                    terminationEndDates.set(c.id, parse(terminatedUpdate.occurred_at.substring(0, 10), 'yyyy-MM-dd', new Date()));
                }
            });

            // Calculate expected per contract based on their individual start date
            // and capped at their termination date if terminated
            activeContracts.forEach(c => {
                // Canonical current weekly instalment (contract + active loan/insurance instalments).
                const weeklyAmount = canonicalWeeklyByContract.get(c.id) ?? weeklyInstalmentByContract.get(c.id) ?? 0;
                const startDate = contractStartDates.get(c.id);

                // Calculate weeks for this contract (from start date to period end)
                let contractWeeks = weeksInPeriod; // Default to full period
                if (startDate) {
                    // starts_at is a DATE column: parse it as a local calendar day so it compares on
                    // the same basis as monthRange (local Dates). new Date('yyyy-MM-dd') is a
                    // UTC-midnight instant, which reads as the previous day at negative UTC offsets
                    // and inflates the expected total for a viewer in, say, New York.
                    const startDateObj = parse(startDate.substring(0, 10), 'yyyy-MM-dd', new Date());
                    const periodStart = monthRange.from;
                    let periodEnd = monthRange.to;

                    // Cap period end at the termination date if contract is terminated
                    const terminationDate = terminationEndDates.get(c.id);
                    if (terminationDate && terminationDate < periodEnd) {
                        periodEnd = terminationDate;
                    }

                    // If contract starts after period start, calculate weeks from contract start
                    if (startDateObj > periodStart) {
                        const effectiveStart = startDateObj;
                        const daysActive = differenceInDays(periodEnd, effectiveStart) + 1;
                        contractWeeks = Math.max(0, Math.ceil(daysActive / 7));
                    } else {
                        // Contract started before or at period start — recalculate if capped
                        if (terminationDate && terminationDate < monthRange.to) {
                            const daysActive = differenceInDays(periodEnd, periodStart) + 1;
                            contractWeeks = Math.max(0, Math.ceil(daysActive / 7));
                        }
                    }
                    // If contract starts after period end, no expected payments
                    if (startDateObj > periodEnd) {
                        contractWeeks = 0;
                    }
                }

                expectedByContract.set(c.id, weeklyAmount * contractWeeks);
            });

            // Loan & insurance-loan instalments are already folded into the canonical weekly
            // amount above (and drop off once a loan is fully repaid), so no separate summation.

            // Sum actual repayments - only count payments on or after contract start date
            repayments.forEach(r => {
                const tx = r.transactions as { id: number; amount: number; transaction_at: string; description: string | null } | null;
                if (tx && r.contract_id) {
                    const contractStart = contractStartDates.get(r.contract_id);
                    // Only count payment if it's on or after the contract's start date
                    const txDate = tx.transaction_at.substring(0, 10);
                    if (!contractStart || txDate >= contractStart) {
                        const current = paidByContract.get(r.contract_id) || { amount: 0, lastPayment: null };
                        current.amount += tx.amount || 0;
                        if (!current.lastPayment || tx.transaction_at > current.lastPayment) {
                            current.lastPayment = tx.transaction_at;
                        }
                        paidByContract.set(r.contract_id, current);

                        // Store individual transaction details
                        const txList = transactionsByContract.get(r.contract_id) || [];
                        txList.push({
                            id: tx.id,
                            amount: tx.amount,
                            transaction_at: tx.transaction_at,
                            description: tx.description,
                        });
                        transactionsByContract.set(r.contract_id, txList);
                    }
                }
            });

            // Build outstanding list
            const outstanding: OutstandingContract[] = [];

            activeContracts.forEach(c => {
                const expected = expectedByContract.get(c.id) || 0;
                const paidInfo = paidByContract.get(c.id) || { amount: 0, lastPayment: null };
                const shortfall = Math.round((expected - paidInfo.amount) * 100) / 100;

                // Only include if shortfall > 0
                if (shortfall > 0) {
                    // THE EMBED IS EVERY PROFILE POINTING AT THE DRIVER, NOT THE DRIVER'S ONE ROW —
                    // see `repaymentsDriver`. Reading it by position printed the owner's ADMIN
                    // profile in this table's Driver column (contract 318 read "Joseph Paddy" for
                    // Peter Azaglo); the name, phone and photo now come off the one row that is
                    // the driver's.
                    const { driver_name: driverName, phone_number: phoneNumber, driver_photo: driverPhoto } =
                        outstandingDriverIdentity(c.drivers);
                    const vehicleReg = (c.vehicles as any)?.vehicle_registrations;
                    const regNumber = Array.isArray(vehicleReg)
                        ? vehicleReg[0]?.number
                        : vehicleReg?.number;

                    // Vehicle photo info
                    const vehicleColor = (c.vehicles as any)?.color || null;
                    const vehicleProcurement = (c.vehicles as any)?.vehicle_procurement;
                    const vehicleListingUrl = Array.isArray(vehicleProcurement)
                        ? vehicleProcurement[0]?.listing_url
                        : vehicleProcurement?.listing_url || null;
                    const vehicleUpdatedAt = (c.vehicles as any)?.updated_at || null;

                    // Get transactions for this contract, sorted by date descending
                    const contractTxs = transactionsByContract.get(c.id) || [];
                    contractTxs.sort((a, b) => new Date(b.transaction_at).getTime() - new Date(a.transaction_at).getTime());

                    // Build instalment weeks for this contract.
                    // Canonical weekly already includes active loan & insurance instalments.
                    const weeklyAmount = canonicalWeeklyByContract.get(c.id) ?? weeklyInstalmentByContract.get(c.id) ?? 0;
                    const totalWeeklyExpected = Math.round(weeklyAmount * 100) / 100;
                    const contractStart = contractStartDates.get(c.id);
                    const instalmentWeeks: InstalmentWeek[] = [];

                    // Calculate weeks within the period for this contract
                    const periodStart = monthRange.from;
                    let periodEnd = monthRange.to;

                    // Cap period end at the termination date if contract is terminated
                    const terminationDate = terminationEndDates.get(c.id);
                    if (terminationDate && terminationDate < periodEnd) {
                        periodEnd = terminationDate;
                    }

                    // Local-calendar parse of the DATE column (see the expected calculation above);
                    // a UTC-midnight instant would start the breakdown a week early at negative
                    // UTC offsets, adding a phantom leading week row.
                    const contractStartObj = contractStart
                        ? parse(contractStart.substring(0, 10), 'yyyy-MM-dd', new Date())
                        : null;
                    const effectiveStart = contractStartObj && contractStartObj > periodStart
                        ? contractStartObj
                        : periodStart;

                    // Generate weekly periods (Monday to Sunday)
                    // Align the start date to the Monday of that week
                    const weekStart = startOfWeek(effectiveStart, { weekStartsOn: 1 });
                    while (weekStart <= periodEnd) {
                        const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
                        const actualWeekEnd = weekEnd > periodEnd ? periodEnd : weekEnd;

                        const weekStartStr = format(weekStart, 'yyyy-MM-dd');
                        const weekEndStr = format(actualWeekEnd, 'yyyy-MM-dd');

                        // Find transactions in this week
                        const weekTxs = contractTxs.filter(tx => {
                            const txDate = tx.transaction_at.substring(0, 10);
                            return txDate >= weekStartStr && txDate <= weekEndStr;
                        });

                        const paidInWeek = weekTxs.reduce((sum, tx) => sum + tx.amount, 0);

                        let status: 'paid' | 'partial' | 'missed' = 'missed';
                        if (paidInWeek >= totalWeeklyExpected) {
                            status = 'paid';
                        } else if (paidInWeek > 0) {
                            status = 'partial';
                        }

                        instalmentWeeks.push({
                            weekStart: weekStartStr,
                            weekEnd: weekEndStr,
                            expectedAmount: totalWeeklyExpected,
                            paidAmount: paidInWeek,
                            transactions: weekTxs,
                            status,
                        });

                        // Move to next week
                        weekStart.setDate(weekStart.getDate() + 7);
                    }

                    outstanding.push({
                        contract_id: c.id,
                        driver_name: driverName,
                        driver_photo: driverPhoto,
                        phone_number: phoneNumber,
                        vehicle_registration: regNumber || null,
                        vehicle_color: vehicleColor,
                        vehicle_listing_url: vehicleListingUrl,
                        vehicle_updated_at: vehicleUpdatedAt,
                        expected,
                        paid: paidInfo.amount,
                        shortfall,
                        last_payment: paidInfo.lastPayment,
                        transactions: contractTxs,
                        instalmentWeeks: instalmentWeeks.filter(w => w.status !== 'paid'),
                    });
                }
            });

            // Calculate summary stats
            const totalOutstanding = outstanding.length;
            const totalShortfall = outstanding.reduce((sum, o) => sum + o.shortfall, 0);
            const totalExpected = Array.from(expectedByContract.values()).reduce((a, b) => a + b, 0);
            const avgShortfall = totalOutstanding > 0 ? totalShortfall / totalOutstanding : 0;

            return {
                outstanding,
                stats: {
                    totalExpected,
                    totalShortfall,
                    avgShortfall,
                },
            };
        },
    });

    // Filter and sort data
    const filteredData = useMemo(() => {
        let data = outstandingData?.outstanding || [];

        // Apply search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            data = data.filter(row =>
                row.driver_name.toLowerCase().includes(query) ||
                row.contract_id.toString().includes(query) ||
                (row.vehicle_registration?.toLowerCase().includes(query) ?? false)
            );
        }

        // Apply status filter
        if (statusFilter === 'PARTIAL') {
            data = data.filter(row => row.paid > 0);
        } else if (statusFilter === 'MISSED') {
            data = data.filter(row => row.paid === 0);
        }

        // Apply sorting
        if (sortKey) {
            data = [...data].sort((a, b) => {
                const aVal = a[sortKey as keyof OutstandingContract];
                const bVal = b[sortKey as keyof OutstandingContract];
                if (aVal === null || aVal === undefined) return 1;
                if (bVal === null || bVal === undefined) return -1;
                if (typeof aVal === 'number' && typeof bVal === 'number') {
                    return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
                }
                return sortDirection === 'asc'
                    ? String(aVal).localeCompare(String(bVal))
                    : String(bVal).localeCompare(String(aVal));
            });
        }

        return data;
    }, [outstandingData, sortKey, sortDirection, searchQuery, statusFilter]);

    // Pagination
    const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);
    const paginatedData = filteredData.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    const stats = outstandingData?.stats;

    if (isLoading) {
        return (
            <div className="space-y-6">
                <div className="grid gap-4 md:grid-cols-5">
                    {[...Array(5)].map((_, i) => (
                        <Skeleton key={i} className="h-28" />
                    ))}
                </div>
                <Skeleton className="h-96" />
            </div>
        );
    }

    // A read that failed, or came back short, must not render as a clean sheet: with no data the
    // table below says every contract is up to date.
    if (error) {
        return (
            <p className="py-8 text-center text-sm text-muted-foreground">
                Collections could not be loaded: {(error as Error).message}
            </p>
        );
    }

    return (
        <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid gap-4 md:grid-cols-5">
                <StatCard
                    title="Total Expected"
                    value={<CurrencyDisplay amount={stats?.totalExpected || 0} />}
                    description="The sum of all scheduled instalment payments due within the selected period across all contracts shown in this view."
                />
                <StatCard
                    title="Total Shortfall"
                    value={<CurrencyDisplay amount={stats?.totalShortfall || 0} />}
                    description="The cumulative gap between what was expected and what was collected. This represents the total amount still owed by drivers."
                />
                <StatCard
                    title="Avg Shortfall"
                    value={<CurrencyDisplay amount={stats?.avgShortfall || 0} />}
                    description="The average outstanding amount per contract. Higher values may indicate systemic collection issues requiring attention."
                />
                <StatCard
                    title="Active Waivers"
                    value={collectionActions?.waivers.length || 0}
                    description="The number of interest waivers currently active for the period. Waivers reduce or eliminate penalty interest for approved cases."
                />
                <StatCard
                    title="Active Notes"
                    value={collectionActions?.notes.length || 0}
                    description="The number of collection notes logged in the period. Notes document communication, payment arrangements, or follow-up actions."
                />
            </div>

            {/* Data Table */}
            <Card className="border-0">
                <CardContent className="pt-6">
                    {/* Search Input and Pagination */}
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2 flex-1">
                            <div className="max-w-sm flex-1">
                                <Input
                                    placeholder="Search driver, contract ID, or vehicle..."
                                    value={searchQuery}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                />
                            </div>
                            <Select
                                value={statusFilter}
                                onValueChange={(val) => {
                                    setStatusFilter(val as 'ALL' | 'PARTIAL' | 'MISSED');
                                    setCurrentPage(1);
                                }}
                            >
                                <SelectTrigger className="w-[140px]">
                                    <div className="flex items-center gap-2">
                                        <Filter className="h-4 w-4 text-muted-foreground" />
                                        <SelectValue placeholder="Status" />
                                    </div>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="ALL">All</SelectItem>
                                    <SelectItem value="PARTIAL">Partial</SelectItem>
                                    <SelectItem value="MISSED">Missed</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <ListPagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalItems={filteredData.length}
                            onPageChange={setCurrentPage}
                        />
                    </div>

                    {collectionActionsError && (
                        <p className="mb-2 text-xs text-muted-foreground">
                            The waivers and notes for this period could not be loaded, so a week that already has one shows without it.
                        </p>
                    )}
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <SortableTableHead
                                        sortKey="driver_name"
                                        currentSortKey={sortKey}
                                        currentSortDirection={sortDirection}
                                        onSort={handleSort}
                                    >
                                        Driver
                                    </SortableTableHead>
                                    <TableHead>Contract</TableHead>
                                    <TableHead>Vehicle</TableHead>
                                    <SortableTableHead
                                        sortKey="expected"
                                        currentSortKey={sortKey}
                                        currentSortDirection={sortDirection}
                                        onSort={handleSort}
                                        align="right"
                                    >
                                        Expected
                                    </SortableTableHead>
                                    <SortableTableHead
                                        sortKey="paid"
                                        currentSortKey={sortKey}
                                        currentSortDirection={sortDirection}
                                        onSort={handleSort}
                                        align="right"
                                    >
                                        Paid
                                    </SortableTableHead>
                                    <SortableTableHead
                                        sortKey="shortfall"
                                        currentSortKey={sortKey}
                                        currentSortDirection={sortDirection}
                                        onSort={handleSort}
                                        align="right"
                                    >
                                        Shortfall
                                    </SortableTableHead>
                                    <TableHead className="w-12"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {paginatedData.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} className="h-32 text-center text-muted-foreground">
                                            All contracts are up to date! 🎉
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginatedData.map(row => {
                                        const isExpanded = expandedRows.has(row.contract_id);
                                        const hasTransactions = row.instalmentWeeks.length > 0;

                                        // Count waivers/notes by checking each instalment week
                                        // (matches exactly what the children display)
                                        let waiverCount = 0;
                                        let noteCount = 0;
                                        row.instalmentWeeks.forEach(week => {
                                            if (getWaiverForWeek(row.contract_id, week.weekStart, week.weekEnd)) waiverCount++;
                                            if (getNoteForWeek(row.contract_id, week.weekStart, week.weekEnd)) noteCount++;
                                        });

                                        return (
                                            <React.Fragment key={row.contract_id}>
                                                <TableRow
                                                    className={hasTransactions ? 'cursor-pointer hover:bg-muted/50' : ''}
                                                    onClick={() => hasTransactions && toggleExpand(row.contract_id)}
                                                >
                                                    <TableCell className="font-medium">
                                                        <div className="flex items-center gap-2">
                                                            {hasTransactions ? (
                                                                isExpanded ? (
                                                                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                                                ) : (
                                                                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                                                )
                                                            ) : (
                                                                <div className="w-4" />
                                                            )}
                                                            <Avatar className="h-7 w-7">
                                                                <SignedAvatarImage src={row.driver_photo || undefined} alt={row.driver_name} />
                                                                <AvatarFallback className="text-xs">
                                                                    {row.driver_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                                                                </AvatarFallback>
                                                            </Avatar>
                                                            {row.driver_name}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <a
                                                            href={`/dashboard/repayments?contract_id=${row.contract_id}`}
                                                            className="text-blue-600 hover:underline"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            #{row.contract_id}
                                                        </a>
                                                    </TableCell>
                                                    <TableCell>
                                                        {row.vehicle_registration ? (
                                                            <div className="flex items-center gap-2">
                                                                {(() => {
                                                                    const photoUrl = getSbtPhotoUrl(row.vehicle_listing_url, row.vehicle_updated_at);
                                                                    if (photoUrl) {
                                                                        return (
                                                                            <HoverCard openDelay={200} closeDelay={100}>
                                                                                <HoverCardTrigger asChild>
                                                                                    <img
                                                                                        src={photoUrl}
                                                                                        alt=""
                                                                                        className="h-7 w-7 rounded-md border border-gray-200 object-cover shrink-0 cursor-zoom-in"
                                                                                        onError={(e) => {
                                                                                            e.currentTarget.style.display = 'none';
                                                                                            e.currentTarget.nextElementSibling?.classList.remove('hidden');
                                                                                        }}
                                                                                    />
                                                                                </HoverCardTrigger>
                                                                                <HoverCardContent side="left" align="center" className="w-auto p-1" sideOffset={8}>
                                                                                    <img src={photoUrl} alt="" className="w-[480px] rounded-md" />
                                                                                </HoverCardContent>
                                                                            </HoverCard>
                                                                        );
                                                                    }
                                                                    return null;
                                                                })()}
                                                                <div
                                                                    className={`h-7 w-7 rounded-md border border-gray-200 shrink-0${
                                                                        getSbtPhotoUrl(row.vehicle_listing_url, row.vehicle_updated_at) ? ' hidden' : ''
                                                                    }`}
                                                                    style={{ backgroundColor: getColorValue(row.vehicle_color || '') || '#e5e7eb' }}
                                                                />
                                                                {row.vehicle_registration}
                                                            </div>
                                                        ) : '-'}
                                                    </TableCell>
                                                    <TableCell className="text-right">{formatCurrency(row.expected)}</TableCell>
                                                    <TableCell className="text-right">{formatCurrency(row.paid)}</TableCell>
                                                    <TableCell className="text-right">
                                                        <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs ${
                                                            row.paid > 0
                                                                ? 'border-yellow-200 bg-yellow-50 text-yellow-700'
                                                                : 'border-red-200 bg-red-50 text-red-600'
                                                        }`}>
                                                            {formatCurrency(row.shortfall)}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell onClick={(e) => e.stopPropagation()}>
                                                        <div className="flex items-center gap-2 text-xs">
                                                            {waiverCount > 0 && (
                                                                <span className="flex items-center gap-0.5 text-green-600">
                                                                    <ShieldCheck className="h-3 w-3" />
                                                                    {waiverCount}
                                                                </span>
                                                            )}
                                                            {noteCount > 0 && (
                                                                <span className="flex items-center gap-0.5 text-amber-600">
                                                                    <StickyNote className="h-3 w-3" />
                                                                    {noteCount}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                                {/* Expanded instalment week details */}
                                                {isExpanded && row.instalmentWeeks.length > 0 && (
                                                    <TableRow className="bg-muted/30 hover:bg-muted/30">
                                                        <TableCell colSpan={7} className="py-3">
                                                            <div className="pl-8 space-y-2">
                                                                <div className="text-xs font-medium text-muted-foreground mb-2">
                                                                    Instalment Weeks ({row.instalmentWeeks.length})
                                                                </div>
                                                                <div className="grid gap-2">
                                                                    {row.instalmentWeeks.map((week, idx) => {
                                                                        // Find waiver/note that covers this specific week
                                                                        const weekWaiver = getWaiverForWeek(row.contract_id, week.weekStart, week.weekEnd);
                                                                        const weekNote = getNoteForWeek(row.contract_id, week.weekStart, week.weekEnd);
                                                                        const hasWaiver = !!weekWaiver;
                                                                        const hasNote = !!weekNote;

                                                                        const statusColors = {
                                                                            paid: 'border-green-200 bg-green-50',
                                                                            partial: 'border-yellow-200 bg-yellow-50',
                                                                            missed: 'border-red-200 bg-red-50',
                                                                        };
                                                                        const statusLabels = {
                                                                            paid: 'Paid',
                                                                            partial: 'Partial',
                                                                            missed: 'Missed',
                                                                        };

                                                                        return (
                                                                            <div
                                                                                key={idx}
                                                                                className={`flex items-center justify-between p-3 rounded-lg border ${statusColors[week.status]}`}
                                                                            >
                                                                                <div className="flex-1 min-w-0">
                                                                                    <div className="flex items-center gap-2">
                                                                                        <span className="text-sm font-medium">
                                                                                            {formatCurrency(week.paidAmount)} / {formatCurrency(week.expectedAmount)}
                                                                                        </span>
                                                                                        <Badge
                                                                                            variant="outline"
                                                                                            className={`text-xs ${week.status === 'paid' ? 'text-green-600 border-green-300' :
                                                                                                week.status === 'partial' ? 'text-yellow-600 border-yellow-300' :
                                                                                                    'text-red-600 border-red-300'
                                                                                                }`}
                                                                                        >
                                                                                            {statusLabels[week.status]}
                                                                                        </Badge>
                                                                                    </div>
                                                                                    <div className="text-xs text-muted-foreground">
                                                                                        {week.transactions.length > 0
                                                                                            ? `${week.transactions.length} transaction(s)`
                                                                                            : 'No transactions'}
                                                                                    </div>
                                                                                </div>
                                                                                <div className="flex flex-col items-end gap-1 ml-4">
                                                                                    <div className="flex items-center gap-2">
                                                                                        {hasWaiver && (
                                                                                            <Badge
                                                                                                variant="outline"
                                                                                                className="text-green-600 border-green-300 bg-green-50 cursor-pointer text-xs"
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    openSheet(row, 'waiver', weekWaiver, week);
                                                                                                }}
                                                                                            >
                                                                                                <ShieldCheck className="h-3 w-3 mr-1" />
                                                                                                Waiver
                                                                                            </Badge>
                                                                                        )}
                                                                                        {hasNote && (
                                                                                            <Badge
                                                                                                variant="outline"
                                                                                                className="text-amber-600 border-amber-300 bg-amber-50 cursor-pointer text-xs"
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    openSheet(row, 'note', weekNote, week);
                                                                                                }}
                                                                                            >
                                                                                                <StickyNote className="h-3 w-3 mr-1" />
                                                                                                Note
                                                                                            </Badge>
                                                                                        )}
                                                                                        <span className="text-xs text-muted-foreground">
                                                                                            {format(parse(week.weekStart, 'yyyy-MM-dd', new Date()), 'MMM d')} - {format(parse(week.weekEnd, 'yyyy-MM-dd', new Date()), 'MMM d, yyyy')}
                                                                                        </span>
                                                                                        <DropdownMenu>
                                                                                            <DropdownMenuTrigger asChild>
                                                                                                <Button variant="ghost" size="icon" className="h-6 w-6">
                                                                                                    <MoreHorizontal className="h-3 w-3" />
                                                                                                </Button>
                                                                                            </DropdownMenuTrigger>
                                                                                            <DropdownMenuContent align="end">
                                                                                                <DropdownMenuItem
                                                                                                    onClick={() => openSheet(row, 'waiver', hasWaiver ? weekWaiver : undefined, week)}
                                                                                                >
                                                                                                    <ShieldCheck className="h-4 w-4 mr-2" />
                                                                                                    {hasWaiver ? 'Edit Waiver' : 'Add Waiver'}
                                                                                                </DropdownMenuItem>
                                                                                                <DropdownMenuItem
                                                                                                    onClick={() => openSheet(row, 'note', hasNote ? weekNote : undefined, week)}
                                                                                                >
                                                                                                    <StickyNote className="h-4 w-4 mr-2" />
                                                                                                    {hasNote ? 'Edit Note' : 'Add Note'}
                                                                                                </DropdownMenuItem>
                                                                                            </DropdownMenuContent>
                                                                                        </DropdownMenu>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </React.Fragment>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            {/* Collection Action Sheet */}
            {sheetData && (
                <CollectionActionSheet
                    open={sheetOpen}
                    onOpenChange={(open) => !open && closeSheet()}
                    type={sheetData.type}
                    contractId={sheetData.row.contract_id}
                    driverName={sheetData.row.driver_name}
                    defaultPeriod={monthRange}
                    existingRecord={sheetData.existingRecord}
                    fixedPeriod={sheetData.fixedPeriod}
                />
            )}
        </div>
    );
}
